import React from "react";
import {
    Box, Typography, Container, Button, Accordion,
    AccordionSummary, AccordionDetails, styled, Stack, useTheme
} from "@mui/material";
import { motion } from "framer-motion";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { FaGlobeAfrica, FaGlobeEurope } from "react-icons/fa";
import { IoLanguage } from "react-icons/io5";

// ==========================================
// 1. STYLED COMPONENTS (Theme-Aware)
// ==========================================

const PageContainer = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.default,
    minHeight: "100vh",
    transition: "background-color 0.4s ease-in-out",
}));

const HeroSection = styled(Box)(({ theme }) => {
    const isSky = theme.palette.background.default === "#BBE0EF";
    return {
        height: "70vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        background: isSky
            ? `linear-gradient(rgba(187, 224, 239, 0.7), rgba(187, 224, 239, 0.8)), 
               url('https://cdn.pixabay.com/photo/2020/07/08/04/12/work-5382501_1280.jpg')`
            : `linear-gradient(rgba(9, 18, 44, 0.7), rgba(9, 18, 44, 0.8)), 
               url('https://cdn.pixabay.com/photo/2020/07/08/04/12/work-5382501_1280.jpg')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
        [theme.breakpoints.down("md")]: {
            backgroundAttachment: "scroll",
        },
    };
});

const GlassOverlay = styled(motion.div)(({ theme }) => ({
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    backdropFilter: "blur(12px)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    padding: theme.spacing(6),
    textAlign: "center",
    color: "#fff",
    maxWidth: "850px",
    borderRadius: "24px",
    margin: "0 20px",
    boxShadow: "0 20px 50px rgba(0,0,0,0.2)",
}));

const ProgramCard = styled(Box)(({ theme }) => {
    const isSky = theme.palette.background.default === "#BBE0EF";
    return {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        backgroundColor: "#FFFFFF",
        padding: theme.spacing(3, 4),
        borderRadius: "16px",
        marginBottom: theme.spacing(2),
        border: isSky ? "1px solid rgba(9, 18, 44, 0.1)" : "1px solid #f0f0f0",
        boxShadow: isSky ? "0 10px 25px rgba(9, 18, 44, 0.05)" : "0 4px 6px rgba(0,0,0,0.02)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
            transform: "translateX(12px)",
            borderColor: "#B8860B",
            boxShadow: "0 15px 35px rgba(9, 18, 44, 0.1)",
        },
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
            textAlign: "center",
            gap: theme.spacing(2),
        },
    };
});

const EnrollButton = styled(Button)({
    backgroundColor: "#09122C",
    color: "#FFF",
    fontWeight: 700,
    textTransform: "none",
    padding: "12px 32px",
    borderRadius: "8px",
    transition: "all 0.3s ease",
    "&:hover": {
        backgroundColor: "#B8860B",
        color: "#000",
        transform: "scale(1.05)",
    },
});

const StyledAccordion = styled(Accordion)(({ theme }) => ({
    backgroundColor: "#0F1A3A",
    color: "#FFFFFF",
    borderRadius: "12px !important",
    marginBottom: "12px",
    border: "1px solid rgba(255,255,255,0.05)",
    boxShadow: "none",
    overflow: "hidden",
    "&:before": { display: "none" },
    "&.Mui-expanded": {
        backgroundColor: "#162447",
        border: "1px solid #B8860B",
    },
}));

// ==========================================
// 2. DATA CONFIGURATION
// ==========================================

const languages = [
    { name: "English", flag: "🇬🇧", route: "/english-for-professionals", regionFocus: "Global • Academic & Career Success" },
    { name: "German", flag: "🇩🇪", route: "/german-language", regionFocus: "Europe • Migration & Work Visas" },
    { name: "Chinese", flag: "🇨🇳", route: "/chinese-language", regionFocus: "Africa • Trade • Global Innovation" },
    { name: "Finnish", flag: "🇫🇮", route: "/finnish-language", regionFocus: "Nordic • Integration & Work" },
    { name: "Yoruba", flag: "🇳🇬", route: "/nigerian-language", regionFocus: "Heritage • Diaspora • Cultural Identity" }
];

const faqData = [
    { q: "Why are these language programs critical?", icon: <FaGlobeAfrica />, a: "They empower professionals and migrants with essential communication skills for relocation, global education, and career advancement." },
    { q: "Do they support IELTS or TOEFL preparation?", icon: <IoLanguage />, a: "Yes. Our English program includes specialized training for IELTS and TOEFL, including mock exams and feedback." },
    { q: "Can beginners enroll?", icon: <FaGlobeEurope />, a: "All programs are beginner-friendly and follow a structured path to advanced fluency, ideal for visa interviews and business." }
];

// ==========================================
// 3. MAIN COMPONENT
// ==========================================

export default function LanguageCourses() {
    const theme = useTheme();
    const isSkyMode = theme.palette.background.default === "#BBE0EF";

    return (
        <PageContainer>
            {/* Hero Section with Theme-Aware Overlay */}
            <HeroSection>
                <GlassOverlay
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                >
                    <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, color: "#fdfdfd", letterSpacing: "-0.04em", fontSize: { xs: "2.2rem", md: "3.5rem" } }}>
                        Master Global Communication
                    </Typography>
                    <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400, mb: 4, maxWidth: "600px", mx: "auto", color: isSkyMode ? "#BBE0EF" : "#FFF" }}>
                        Excel in today’s competitive job market with essential language skills designed for relocation and trade.
                    </Typography>
                </GlassOverlay>
            </HeroSection>

            {/* Language Showcase Section */}
            <Box sx={{ py: 12 }}>
                <Container maxWidth="md">
                    <Stack spacing={1} sx={{ textAlign: "center", mb: 8 }}>
                        <Typography sx={{ color: "#B8860B", fontWeight: 800, letterSpacing: 3, fontSize: "0.85rem" }}>
                            COURSE OVERVIEW
                        </Typography>
                        <Typography variant="h3" sx={{ fontWeight: 900, color: "#09122C", letterSpacing: "-0.02em" }}>
                            Our Language Programs
                        </Typography>
                    </Stack>

                    {languages.map((lang, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.1, duration: 0.5 }}
                        >
                            <ProgramCard>
                                <Box>
                                    <Typography variant="h5" sx={{ fontWeight: 900, color: "#09122C", mb: 0.5, display: "flex", alignItems: "center", gap: 1.5 }}>
                                        <span style={{ fontSize: "1.8rem" }}>{lang.flag}</span> {lang.name}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: isSkyMode ? "#09122C" : "#555", fontWeight: 600, opacity: 0.8 }}>
                                        {lang.regionFocus}
                                    </Typography>
                                </Box>
                                <EnrollButton href={lang.route} variant="contained" disableElevation>
                                    View Program
                                </EnrollButton>
                            </ProgramCard>
                        </motion.div>
                    ))}
                </Container>
            </Box>

            {/* FAQ Section - Contrast Anchor */}
            <Box sx={{ py: 12, backgroundColor: "#09122C", position: "relative", overflow: "hidden" }}>
                {/* Background Decoration */}
                <Box sx={{ position: "absolute", right: -100, bottom: -100, width: 400, height: 400, borderRadius: "50%", border: "2px solid rgba(184, 134, 11, 0.1)" }} />

                <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
                    <Typography variant="h4" sx={{ fontWeight: 900, color: "#FFF", mb: 6, textAlign: "center", letterSpacing: "-0.01em" }}>
                        Expert Insights & FAQ
                    </Typography>

                    {faqData.map((qa, index) => (
                        <StyledAccordion key={index}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#B8860B" }} />}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Box sx={{ color: "#B8860B", fontSize: "1.4rem", display: "flex" }}>{qa.icon}</Box>
                                    <Typography sx={{ fontWeight: 800, fontSize: "1.05rem" }}>{qa.q}</Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pb: 3 }}>
                                <Typography sx={{ opacity: 0.85, lineHeight: 1.8, borderLeft: "3px solid #B8860B", pl: 3, color: "#BBE0EF" }}>
                                    {qa.a}
                                </Typography>
                            </AccordionDetails>
                        </StyledAccordion>
                    ))}
                </Container>
            </Box>
        </PageContainer>
    );
}
